import React, { useRef, useEffect } from 'react';
import * as THREE from 'three';
import useIsMobile from '../../hooks/useIsMobile';

/** Contact page — particle vortex / tunnel into the page centre.
 *  Rainbow gradient (violet→cyan→green→gold→magenta) flows through the spiral.
 *  Mouse moves the centre + adjusts speed.
 */
const PARTICLES = 1800;

const VortexBg = () => {
    const mountRef = useRef(null);
    const isMobile = useIsMobile();

    useEffect(() => {
        if (isMobile) return;
        const mount = mountRef.current;
        if (!mount) return;
        const w = mount.clientWidth, h = mount.clientHeight;

        const scene = new THREE.Scene();
        const camera = new THREE.PerspectiveCamera(55, w / h, 0.1, 200);
        camera.position.z = 22;

        const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        renderer.setSize(w, h);
        renderer.setClearColor(0x000000, 0);
        mount.appendChild(renderer.domElement);

        const positions = new Float32Array(PARTICLES * 3);
        const colours = new Float32Array(PARTICLES * 3);
        const user = []; // {r, theta, z, speed}
        const palette = [
            [0.48, 0.38, 1.0],  // violet
            [0.0, 0.9, 0.8],    // cyan
            [0.0, 0.85, 0.4],   // green
            [1.0, 0.84, 0.0],   // gold
            [1.0, 0.24, 0.54],  // magenta
        ];

        for (let i = 0; i < PARTICLES; i++) {
            const z = (Math.random() - 0.5) * 40;
            const r = 1 + Math.random() * 14;
            const theta = Math.random() * Math.PI * 2;
            user.push({ r, theta, z, speed: 0.18 + Math.random() * 0.25, baseR: r });
            positions[i * 3] = r * Math.cos(theta);
            positions[i * 3 + 1] = r * Math.sin(theta);
            positions[i * 3 + 2] = z;
            const palIdx = Math.floor((Math.abs(z) / 20) * palette.length) % palette.length;
            const c = palette[palIdx];
            colours[i * 3] = c[0];
            colours[i * 3 + 1] = c[1];
            colours[i * 3 + 2] = c[2];
        }

        const geo = new THREE.BufferGeometry();
        geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        geo.setAttribute('color', new THREE.BufferAttribute(colours, 3));
        const mat = new THREE.PointsMaterial({
            size: 0.10,
            vertexColors: true,
            transparent: true,
            opacity: 0.85,
            blending: THREE.AdditiveBlending,
            depthWrite: false,
        });
        const points = new THREE.Points(geo, mat);
        scene.add(points);

        // Mouse
        const mouse = { x: 0, y: 0, tx: 0, ty: 0, sp: 1 };
        const onMouseMove = (e) => {
            const rect = mount.getBoundingClientRect();
            mouse.tx = (e.clientX - rect.left) / rect.width - 0.5;
            mouse.ty = (e.clientY - rect.top) / rect.height - 0.5;
            mouse.sp = 1 + Math.abs(mouse.tx) * 1.5 + Math.abs(mouse.ty) * 1.2;
        };
        window.addEventListener('mousemove', onMouseMove);

        const onResize = () => {
            const W = mount.clientWidth, H = mount.clientHeight;
            renderer.setSize(W, H);
            camera.aspect = W / H;
            camera.updateProjectionMatrix();
        };
        window.addEventListener('resize', onResize);

        let raf = 0;
        const tick = () => {
            raf = requestAnimationFrame(tick);
            mouse.x += (mouse.tx - mouse.x) * 0.04;
            mouse.y += (mouse.ty - mouse.y) * 0.04;

            const arr = geo.attributes.position.array;
            const colArr = geo.attributes.color.array;
            for (let i = 0; i < PARTICLES; i++) {
                const u = user[i];
                u.theta += 0.005 * u.speed * mouse.sp;
                u.z += 0.08 * mouse.sp;
                if (u.z > 22) u.z = -22;
                const xx = u.r * Math.cos(u.theta);
                const yy = u.r * Math.sin(u.theta);
                arr[i * 3] = xx + mouse.x * 2.0;
                arr[i * 3 + 1] = yy - mouse.y * 1.5;
                arr[i * 3 + 2] = u.z;
                // Cycle colour through palette based on z
                const palIdx = Math.floor(((u.z + 22) / 44) * palette.length) % palette.length;
                const c = palette[palIdx];
                colArr[i * 3] = c[0];
                colArr[i * 3 + 1] = c[1];
                colArr[i * 3 + 2] = c[2];
            }
            geo.attributes.position.needsUpdate = true;
            geo.attributes.color.needsUpdate = true;

            points.rotation.z += 0.0008 * mouse.sp;
            renderer.render(scene, camera);
        };
        tick();

        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('resize', onResize);
            if (mount && renderer.domElement.parentNode === mount) {
                mount.removeChild(renderer.domElement);
            }
            geo.dispose();
            mat.dispose();
            renderer.dispose();
        };
    }, [isMobile]);

    if (isMobile) {
        return (
            <div
                className="absolute inset-0 pointer-events-none"
                style={{
                    background:
                        'radial-gradient(circle at 50% 50%, rgba(37,99,235,0.12), transparent 55%), radial-gradient(circle at 30% 30%, rgba(56,189,248,0.10), transparent 55%), radial-gradient(circle at 70% 80%, rgba(14,165,233,0.10), transparent 55%)',
                }}
                aria-hidden="true"
            />
        );
    }

    return (
        <div
            ref={mountRef}
            className="absolute inset-0 w-full h-full pointer-events-none"
            aria-hidden="true"
            data-testid="bg-vortex"
        />
    );
};

export default VortexBg;
